import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { resolveOrganizationId } from "../../Utils/organizationSession";
import "./StudentPortal.css";

/** Return an http(s) URL, or empty string if the value is not a web link. */
export function httpUrl(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^https?:\/\//i.test(text)) return text;
  if (text.startsWith("//")) return `https:${text}`;
  if (/^(www\.)/i.test(text) || /^[a-z0-9][-a-z0-9.]*\.[a-z]{2,}(\/|$)/i.test(text)) {
    return `https://${text.replace(/^\/+/, "")}`;
  }
  return "";
}

/** Photo cover with initials/gradient fallback when the URL is missing or broken. */
export function ProgramCover({
  src,
  alt = "",
  className = "",
  style,
  children,
  overlay,
}) {
  const url = httpUrl(src);
  const [broken, setBroken] = useState(false);
  const showPhoto = Boolean(url) && !broken;

  return (
    <div
      className={`${className}${showPhoto ? " has-photo" : ""}`.trim()}
      style={style}
    >
      {showPhoto ? (
        <img src={url} alt={alt} onError={() => setBroken(true)} />
      ) : (
        children
      )}
      {overlay}
    </div>
  );
}

/** Format portal money as `INR 35,000.00` (code first, no currency symbol). */
export function formatPortalMoney(value, currencyCode = "INR") {
  const { code, amount } = formatPortalMoneyParts(value, currencyCode);
  return `${code} ${amount}`;
}

/** Split currency code + amount for styling (e.g. smaller code). */
export function formatPortalMoneyParts(value, currencyCode = "INR") {
  const n = Number(value);
  const code = String(currencyCode || "INR").trim().toUpperCase() || "INR";
  const amount = Number.isFinite(n)
    ? n.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "0.00";
  return { code, amount };
}
export function formatPortalDate(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return `${iso[3]}-${iso[2]}-${iso[1]}`;
  }
  const dmySlash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmySlash) {
    return `${dmySlash[1]}-${dmySlash[2]}-${dmySlash[3]}`;
  }
  const dmyDash = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyDash) {
    return s;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }
  return s;
}

/** Format clock times as 12-hour with AM/PM (e.g. 13:40 → 1:40 PM). */
export function formatPortalTime(value, timezoneLabel = "") {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (!s || s === "-") return "";
  let clock = s;
  if (/am|pm/i.test(s)) {
    clock = s.replace(/\s*(am|pm)\s*$/i, (m) => ` ${m.trim().toUpperCase()}`);
  } else {
    const m = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return s;
    const h = parseInt(m[1], 10);
    const min = m[2];
    if (Number.isNaN(h) || h > 23) return s;
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    clock = `${h12}:${min} ${ampm}`;
  }
  const tz = String(timezoneLabel || "").trim();
  return tz ? `${clock} ${tz}` : clock;
}

/** Resolve certificate file URL for download. */
export function resolveCertificateUrl(value) {
  if (value == null || String(value).trim() === "") return null;
  let s = String(value).trim();

  s = s.replace(
    /\/storage\/certificates\//i,
    "/storage/app/public/certificates/"
  );

  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/")) return `${MAIN_URL}${s}`;
  if (
    s.startsWith("storage/app/public/") ||
    s.startsWith("certificates/")
  ) {
    const path = s.startsWith("certificates/")
      ? `storage/app/public/${s}`
      : s;
    return `${MAIN_URL}/${path.replace(/^\/+/, "")}`;
  }
  if (s.startsWith("storage/")) {
    return `${MAIN_URL}/storage/app/public/${s.replace(/^storage\//, "")}`;
  }
  return `${MAIN_URL}/storage/app/public/certificates/${s.replace(/^\/+/, "")}`;
}

/** Authenticated certificate download (forces browser save). */
export async function downloadPortalCertificate(
  certificateId,
  suggestedName = "certificate",
  publicUrl = null
) {
  const id = Number(certificateId);
  if (!id) {
    throw new Error("Certificate id is missing.");
  }

  const headers = getApiHeaders();
  // Avoid forcing JSON content-type on binary file downloads.
  delete headers["Content-Type"];
  delete headers["content-type"];

  let arrayBuffer;
  let contentType = "";
  let disposition = "";

  try {
    const res = await axios.get(
      `${MAIN_URL}/api/ems/portal/certificates/${id}/download`,
      {
        headers,
        responseType: "arraybuffer",
        // Treat non-2xx as errors so we can fall back to public URL.
        validateStatus: (status) => status >= 200 && status < 300,
      }
    );
    arrayBuffer = res.data;
    contentType = String(res.headers?.["content-type"] || "");
    disposition = String(res.headers?.["content-disposition"] || "");
  } catch (err) {
    const data = err?.response?.data;
    if (data) {
      try {
        const text =
          typeof data === "string"
            ? data
            : data instanceof ArrayBuffer
              ? new TextDecoder().decode(data)
              : data instanceof Blob
                ? await data.text()
                : "";
        if (text) {
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            // Keep message, but still try public URL fallback below.
            err.apiMessage = parsed.message;
          }
        }
      } catch {
        /* ignore parse errors */
      }
    }

    // Fallback: public storage URL (works when API route is not deployed yet).
    const fallbackUrl = resolveCertificateUrl(publicUrl);
    if (!fallbackUrl) {
      throw new Error(
        err.apiMessage ||
          err?.message ||
          "Could not download certificate."
      );
    }

    const fallbackRes = await fetch(fallbackUrl, {
      method: "GET",
      headers: {
        Authorization: headers.Authorization || "",
        "X-Application-Key": headers["X-Application-Key"] || "",
      },
    });
    if (!fallbackRes.ok) {
      throw new Error(
        err.apiMessage || "Could not download certificate."
      );
    }
    arrayBuffer = await fallbackRes.arrayBuffer();
    contentType = fallbackRes.headers.get("content-type") || "";
    disposition = fallbackRes.headers.get("content-disposition") || "";
  }

  const bytes = new Uint8Array(arrayBuffer || []);
  const head = String.fromCharCode(...bytes.slice(0, 5));
  const looksLikePdf = head.startsWith("%PDF");
  const looksLikeJson =
    contentType.includes("application/json") ||
    head.startsWith("{") ||
    head.startsWith("[");
  const looksLikeHtml =
    contentType.includes("text/html") ||
    head.toLowerCase().startsWith("<!doc") ||
    head.toLowerCase().startsWith("<html");

  if (!looksLikePdf && (looksLikeJson || looksLikeHtml || bytes.length < 8)) {
    let message = "Certificate file is invalid or unavailable.";
    if (looksLikeJson) {
      try {
        message =
          JSON.parse(new TextDecoder().decode(bytes))?.message || message;
      } catch {
        /* ignore */
      }
    }
    throw new Error(message);
  }

  let fileName = suggestedName || "certificate";
  const match = String(disposition).match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
  );
  if (match) {
    fileName = decodeURIComponent(match[1] || match[2] || fileName);
  }
  if (!/\.\w+$/.test(fileName)) {
    fileName = `${fileName}${looksLikePdf ? ".pdf" : ""}`;
  }

  const blob = new Blob([arrayBuffer], {
    type: looksLikePdf
      ? "application/pdf"
      : contentType || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function useStudentPortalFetch(path, extraParams = {}) {
  const { userData } = useAuthStore();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({
    display_timezone: null,
    timezone_label: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const extraParamsKey = JSON.stringify(extraParams || {});

  const fetchData = useCallback(async () => {
    const orgId = resolveOrganizationId(userData);
    let extra = {};
    try {
      extra = extraParamsKey ? JSON.parse(extraParamsKey) : {};
    } catch {
      extra = {};
    }

    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${MAIN_URL}/api/ems/portal/${path}`, {
        headers: getApiHeaders(),
        params: {
          ...(orgId ? { organization_id: orgId } : {}),
          ...extra,
        },
      });
      const payload = res?.data?.data ?? null;
      setData(payload);
      const fromPayload =
        payload && !Array.isArray(payload) && typeof payload === "object"
          ? payload
          : {};
      setMeta({
        display_timezone:
          res?.data?.display_timezone ??
          fromPayload.display_timezone ??
          null,
        timezone_label:
          res?.data?.timezone_label ?? fromPayload.timezone_label ?? null,
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load data."
      );
    } finally {
      setLoading(false);
    }
  }, [path, userData, extraParamsKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, meta, loading, error, refetch: fetchData, setData };
}

export function StudentPortalShell({
  title,
  subtitle,
  headerRight,
  tags,
  children,
  className,
  hideHeader = false,
}) {
  return (
    <div className={`ems-student-portal${className ? ` ${className}` : ""}`}>
      {!hideHeader ? (
        <div className="ems-student-portal-header">
          <div className="ems-student-portal-header-row">
            <div className="ems-student-portal-header-main">
              <h1>{title}</h1>
              {subtitle ? <p>{subtitle}</p> : null}
              {tags ? <div className="ems-student-portal-tags">{tags}</div> : null}
            </div>
            {headerRight ? (
              <div className="ems-student-portal-header-right">{headerRight}</div>
            ) : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function StudentPortalState({ loading, error, empty, emptyText, children }) {
  if (loading) {
    return <div className="ems-student-portal-state">Loading…</div>;
  }
  if (error) {
    return <div className="ems-student-portal-state ems-student-portal-error">{error}</div>;
  }
  if (empty) {
    return (
      <div className="ems-student-portal-state">
        {emptyText || "Nothing to show yet."}
      </div>
    );
  }
  return children;
}

export default useStudentPortalFetch;
